import { Button, Card, Field, PageHeader, SelectField, StatusPill } from '@homecare/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import type { credentialTypeSchema } from '@homecare/contracts';
import type { z } from 'zod';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

/**
 * The credentials this agency requires (§6.3.5).
 *
 * ## Why this screen exists
 *
 * §6.3.5 says credential types are **agency-editable data, not an enum**, and
 * the schema and the API were built that way — a table, a create endpoint, a
 * patch endpoint. Nothing called the patch endpoint. An agency could be given
 * eleven types by the provisioning script and then never change one, which is
 * an enum with a migration in front of it.
 *
 * `endpoint-reachability.spec.ts` is what found that, on its first run.
 *
 * ## What an agency can and cannot change
 *
 * `key` is fixed after creation. It is what the compliance evaluation and the
 * onboarding checklist join on, and renaming it would silently detach every
 * credential already recorded against it. The **name** is free — that is the
 * word people read — and so are the two decisions that matter:
 *
 * - **blocks scheduling** — whether missing or lapsed refuses a shift. Some
 *   states require a TB test; some do not. This is the lever for that, and it
 *   is why the field has no default in the contract: an agency has to say.
 * - **active** — a type no longer required stops appearing on new caregivers
 *   without deleting the history of everyone who holds one. Hard rule 8.
 *
 * Turning off `blocksScheduling` does not make the credential invisible; it
 * moves from the refusal to the warning list, which is what somebody expects
 * when they say "we still track it, it just doesn't stop anybody".
 */

type CredentialType = z.infer<typeof credentialTypeSchema>;

const CATEGORY_LABEL: Record<CredentialType['category'], string> = {
  LICENSE: 'Licence',
  CERTIFICATION: 'Certification',
  HEALTH_SCREENING: 'Health screening',
  BACKGROUND_CHECK: 'Background check',
  TRAINING: 'Training',
  DOCUMENT: 'Document',
};

/**
 * "warns from 90 days before it expires", not "[90,60,30,14,7]".
 *
 * §6.3.5 fires a reminder at each of several thresholds. The list is the
 * schedule; what somebody scanning this page wants is when the chasing starts.
 */
function describeWarnings(days: readonly number[]): string {
  if (days.length === 0) return 'no expiry reminders';

  return `warns from ${Math.max(...days)} days before it expires`;
}

export function CredentialTypesPage(): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const canEdit = useHasPermission('credential:update');

  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const types = useQuery({
    queryKey: ['credential-types'],
    queryFn: () => api.get<{ credentialTypes: CredentialType[] }>('/v1/credential-types'),
    staleTime: 0,
  });

  const patch = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Record<string, unknown> }) =>
      api.send('PATCH', `/v1/credential-types/${id}`, changes),
    onSuccess: async () => {
      setFailure(null);
      await queryClient.invalidateQueries({ queryKey: ['credential-types'] });
    },
    onError: setFailure,
  });

  const rows = types.data?.credentialTypes ?? [];

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Credential types"
        meta="What this agency requires of a caregiver, and which of those stop a shift."
        action={
          canEdit && !adding ? (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              Add a credential type
            </Button>
          ) : undefined
        }
      />

      <ErrorNote error={failure ?? types.error} nextStep="Reload the page and try again." />

      {adding && (
        <AddCredentialType
          onDone={async () => {
            await queryClient.invalidateQueries({ queryKey: ['credential-types'] });
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <Card>
        {types.isPending ? (
          <p className="text-ink-3 py-8 text-center text-sm">Loading credential types…</p>
        ) : rows.length === 0 ? (
          <p className="text-ink-3 py-8 text-center text-sm">
            No credential types yet. Add the ones your state requires — a TB test and a background
            check are usually the first two.
          </p>
        ) : (
          <ul className="grid gap-2" data-testid="credential-type-list">
            {rows.map((type) => (
              <li
                key={type.id}
                className="border-line grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_auto] sm:items-center"
                data-testid={`credential-type-${type.key}`}
              >
                <div>
                  <p className="text-ink text-sm font-semibold">
                    {type.name}
                    {!type.isActive && (
                      <span className="ml-2">
                        <StatusPill tone="inactive">Not required</StatusPill>
                      </span>
                    )}
                  </p>
                  <p className="text-ink-3 mt-0.5 text-xs">
                    {CATEGORY_LABEL[type.category]} · <code>{type.key}</code> ·{' '}
                    {describeWarnings(type.warnDaysBefore)}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-ink-2 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={type.blocksScheduling}
                      disabled={!canEdit || patch.isPending}
                      onChange={(event) =>
                        patch.mutate({
                          id: type.id,
                          changes: { blocksScheduling: event.target.checked },
                        })
                      }
                    />
                    Blocks scheduling
                  </label>

                  <label className="text-ink-2 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={type.isActive}
                      disabled={!canEdit || patch.isPending}
                      onChange={(event) =>
                        patch.mutate({ id: type.id, changes: { isActive: event.target.checked } })
                      }
                    />
                    Still required
                  </label>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function AddCredentialType({
  onDone,
  onCancel,
}: {
  onDone: () => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const api = useApi();

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  // No default. §6.3.5 makes this the decision that separates a credential
  // people chase from one that refuses a shift, and a checkbox that arrives
  // ticked is a decision nobody made.
  const [blocks, setBlocks] = useState(false);
  const [category, setCategory] = useState<CredentialType['category']>('TRAINING');
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);

    try {
      // `warnDaysBefore` is left to the contract's default — §6.3.5's
      // 90/60/30/14/7. An agency that wants a different reminder schedule is a
      // real need and a later one; asking for it here would put a five-number
      // list in front of somebody adding a training record.
      await api.send('POST', '/v1/credential-types', {
        key: key.trim(),
        name: name.trim(),
        category,
        blocksScheduling: blocks,
      });

      await onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">Add a credential type</h2>

      <ErrorNote error={failure} nextStep="Check the key is not already in use." />

      <form
        className="mt-4 grid gap-4"
        data-testid="credential-type-form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field
          label="Name"
          hint="What people call it. This is the wording on the caregiver record."
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            // Suggested, not imposed — an agency that wants a different key can
            // still type one, and after this it is fixed for good.
            if (!touched(key, name)) setKey(slugify(event.target.value));
          }}
          required
        />

        <Field
          label="Key"
          hint="Fixed once saved: compliance and the onboarding checklist join on it."
          value={key}
          onChange={(event) => setKey(event.target.value)}
          required
        />

        <SelectField
          label="Kind"
          hint="What sort of thing this is. It groups the record and nothing more."
          value={category}
          onChange={(event) => setCategory(event.target.value as CredentialType['category'])}
        >
          {Object.entries(CATEGORY_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>

        <label className="text-ink-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={blocks}
            onChange={(event) => setBlocks(event.target.checked)}
          />
          Missing or expired stops this caregiver being scheduled
        </label>

        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            pending={busy}
            {...(name.trim() === '' || key.trim() === ''
              ? { disabled: true as const, disabledReason: 'A name and a key are needed.' }
              : {})}
          >
            Add it
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            {...(busy ? { disabled: true as const, disabledReason: 'Saving…' } : {})}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Has somebody edited the key by hand, so the suggestion should stop? */
function touched(key: string, name: string): boolean {
  return key !== '' && key !== slugify(name.slice(0, name.length - 1)) && key !== slugify(name);
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
